import React, { useState, useEffect, useMemo } from 'react';
import { 
  Container, 
  Typography, 
  Box, 
  Paper, 
  useTheme,
  Card,
  CardContent,
  Divider,
  Button,
  Chip,
  Stack,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  LinearProgress,
  Tabs,
  Tab,
  TextField,
  InputAdornment,
  TableSortLabel,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  OutlinedInput,
  IconButton,
  Tooltip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch
} from '@mui/material';
import Grid from '@mui/material/Grid';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { getUserProfile, UserProfile } from '../firebase/services';
import ReceiptIcon from '@mui/icons-material/Receipt';
import PaymentIcon from '@mui/icons-material/Payment';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import HistoryIcon from '@mui/icons-material/History';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import DateRangeIcon from '@mui/icons-material/DateRange';
import DescriptionIcon from '@mui/icons-material/Description';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import ClearIcon from '@mui/icons-material/Clear';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import BarChartIcon from '@mui/icons-material/BarChart';
import TimelineIcon from '@mui/icons-material/Timeline';
import PeopleIcon from '@mui/icons-material/People';
import StorageIcon from '@mui/icons-material/Storage';
import SpeedIcon from '@mui/icons-material/Speed';
import WarningIcon from '@mui/icons-material/Warning';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import GetAppIcon from '@mui/icons-material/GetApp';
import CircleIcon from '@mui/icons-material/Circle';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import VerifiedIcon from '@mui/icons-material/Verified';
import BusinessIcon from '@mui/icons-material/Business';
import EmailIcon from '@mui/icons-material/Email';
import EventIcon from '@mui/icons-material/Event';
import RocketIcon from '@mui/icons-material/Rocket';
import DomainIcon from '@mui/icons-material/Domain';
import CloudIcon from '@mui/icons-material/Cloud';
import ChatIcon from '@mui/icons-material/Chat';
import GroupIcon from '@mui/icons-material/Group';
import BackupIcon from '@mui/icons-material/Backup';
import NotificationsIcon from '@mui/icons-material/Notifications';
import HelpIcon from '@mui/icons-material/Help';
import PhoneIcon from '@mui/icons-material/Phone';
import LiveHelpIcon from '@mui/icons-material/LiveHelp';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import FolderIcon from '@mui/icons-material/Folder';
import SecurityIcon from '@mui/icons-material/Security';
import PublicIcon from '@mui/icons-material/Public';
import StorageOutlinedIcon from '@mui/icons-material/StorageOutlined';
import AssessmentIcon from '@mui/icons-material/Assessment';
import NotificationImportantIcon from '@mui/icons-material/NotificationImportant';
import PersonIcon from '@mui/icons-material/Person';
import { Link } from 'react-router-dom';
import DashboardIcon from '@mui/icons-material/Dashboard';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import DataUsageIcon from '@mui/icons-material/DataUsage';

// Type pour les informations de facturation
interface BillingInfo {
  currentPlan: string;
  planPrice: string;
  nextBilling: string;
  paymentMethod: string;
  cardLastFour: string;
  cardExpiry: string;
  billingAddress: string;
  autoRenew: boolean;
}

// Type pour les factures
interface Invoice {
  id: string;
  invoiceNumber: string;
  date: string;
  amount: string;
  status: 'paid' | 'pending' | 'overdue';
  items: InvoiceItem[];
}

// Type pour les éléments de facture
interface InvoiceItem {
  description: string;
  quantity: number;
  unitPrice: string;
  total: string;
}

// Type pour l'utilisation des services
interface UsageData {
  service: string;
  used: number;
  total: number;
  percentage: number;
}

interface PlanFeature {
  name: string;
  included: boolean;
  limit?: string;
}

interface Plan {
  id: string;
  name: string;
  price: string;
  billingPeriod: string;
  description: string;
  features: PlanFeature[];
  recommended?: boolean;
}

// Mise à jour des données utilisateur
const userData = {
  fullName: "Loïc Ettinger",
  company: "Ares Code Works",
  email: "loic.ettinger@icloud.com",
  createdAt: "2024-12-12",
  accountStatus: "verified"
};

// Mise à jour des données d'abonnement
const subscriptionData = {
  plan: "Pro Developer",
  type: "Mensuel",
  renewal: "Automatique",
  price: "12,99 €",
  nextDate: "2025-07-15"
};

// Fonctionnalités du plan
const planFeatures = [
  "Accès illimité aux apps iOS/Android/Web",
  "Base de données Firebase en temps réel",
  "Hébergement inclus (Firebase Hosting)",
  "Chatbot AI & API personnalisables",
  "Jusqu'à 10 coéquipiers actifs",
  "Sauvegardes automatiques (30j)"
];

// Modules complémentaires
const addOns = [
  {
    icon: <SecurityIcon />,
    name: "Slot de projet supplémentaire",
    description: "+1 projet actif",
    price: "2,99€/mois"
  },
  {
    icon: <PublicIcon />,
    name: "Nom de domaine personnalisé",
    description: "",
    price: "5€/mois"
  },
  {
    icon: <StorageOutlinedIcon />,
    name: "Extension de stockage (100Go)",
    description: "",
    price: "4€/mois"
  },
  {
    icon: <AssessmentIcon />,
    name: "Analytics temps réel (Beta)",
    description: "",
    price: "Gratuit pour les comptes Pro"
  }
];

// Notifications
const notifications = [
  {
    type: "payment",
    message: "Rappel de paiement envoyé le 13 juin 2025",
    date: "2025-06-13",
    icon: <NotificationsIcon color="info" />
  },
  {
    type: "plan",
    message: "Dernière modification de plan le 15 avril 2025",
    date: "2025-04-15",
    icon: <NotificationImportantIcon color="primary" />
  }
];

// Ajout des données simulées pour les factures récentes
const recentInvoices = [
  {
    id: 'INV-2025-006',
    date: '2025-06-15',
    amount: 12.99,
    status: 'paid',
    description: 'Pro Plan - Mensuel'
  },
  {
    id: 'INV-2025-005',
    date: '2025-05-15',
    amount: 12.99,
    status: 'paid',
    description: 'Pro Plan - Mensuel'
  },
  {
    id: 'INV-2025-004',
    date: '2025-04-15',
    amount: 12.99,
    status: 'paid',
    description: 'Pro Plan - Mensuel'
  },
  {
    id: 'INV-2025-003',
    date: '2025-03-15',
    amount: 12.99,
    status: 'paid',
    description: 'Pro Plan - Mensuel'
  }
];

const BillingClient: React.FC = () => {
  const { currentUser } = useAuth();
  const { t } = useLanguage();
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [billingInfo, setBillingInfo] = useState<BillingInfo | null>(null);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [usageData, setUsageData] = useState<UsageData[]>([]);
  const [tabValue, setTabValue] = useState(0);
  
  // Nouveaux états pour la recherche et le tri
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [orderBy, setOrderBy] = useState<keyof Invoice>('date');
  const [order, setOrder] = useState<'asc' | 'desc'>('desc');
  
  // Nouveaux états pour l'onglet Usage
  const [usageSearchQuery, setUsageSearchQuery] = useState('');
  const [usageFilterThreshold, setUsageFilterThreshold] = useState<string>('all');

  const [openPlanDialog, setOpenPlanDialog] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<Plan | null>(null);

  // Statistiques mensuelles simulées
  const monthlyStats = {
    apiCalls: {
      current: 850000,
      previous: 720000,
      change: 18.06
    },
    storage: {
      current: 75,
      previous: 65,
      change: 15.38
    },
    activeUsers: {
      current: 45,
      previous: 38,
      change: 18.42
    },
    bandwidth: {
      current: 320,
      previous: 280,
      change: 14.29
    }
  };

  // Helper function to get string value from translation
  const getTranslationString = (key: string, defaultValue: string = ''): string => {
    const translation = t(key);
    return typeof translation === 'string' ? translation : defaultValue;
  };

  // Plans disponibles
  const plans = useMemo<Plan[]>(() => [
    {
      id: 'starter',
      name: getTranslationString('billing.plans.starter.name', 'Starter'),
      price: '€99',
      billingPeriod: getTranslationString('billing.monthly', '/month'),
      description: getTranslationString('billing.plans.starter.description', 'Perfect for small businesses getting started'),
      features: [
        { name: getTranslationString('billing.features.apiCalls', 'API Calls'), included: true, limit: '100,000/month' },
        { name: getTranslationString('billing.features.storage', 'Storage'), included: true, limit: '10 GB' },
        { name: getTranslationString('billing.features.users', 'User Accounts'), included: true, limit: '10' },
        { name: getTranslationString('billing.features.support', 'Email Support'), included: true },
        { name: getTranslationString('billing.features.analytics', 'Basic Analytics'), included: true },
        { name: getTranslationString('billing.features.customization', 'Advanced Customization'), included: false }
      ]
    },
    {
      id: 'professional',
      name: getTranslationString('billing.plans.professional.name', 'Professional'),
      price: '€299',
      billingPeriod: getTranslationString('billing.monthly', '/month'),
      description: getTranslationString('billing.plans.professional.description', 'Ideal for growing companies'),
      features: [
        { name: getTranslationString('billing.features.apiCalls', 'API Calls'), included: true, limit: '500,000/month' },
        { name: getTranslationString('billing.features.storage', 'Storage'), included: true, limit: '50 GB' },
        { name: getTranslationString('billing.features.users', 'User Accounts'), included: true, limit: '25' },
        { name: getTranslationString('billing.features.support', 'Priority Support'), included: true },
        { name: getTranslationString('billing.features.analytics', 'Advanced Analytics'), included: true },
        { name: getTranslationString('billing.features.customization', 'Advanced Customization'), included: true }
      ],
      recommended: true
    },
    {
      id: 'enterprise',
      name: getTranslationString('billing.plans.enterprise.name', 'Enterprise'),
      price: '€499',
      billingPeriod: getTranslationString('billing.monthly', '/month'),
      description: getTranslationString('billing.plans.enterprise.description', 'For large-scale operations'),
      features: [
        { name: getTranslationString('billing.features.apiCalls', 'API Calls'), included: true, limit: '1,000,000/month' },
        { name: getTranslationString('billing.features.storage', 'Storage'), included: true, limit: '100 GB' },
        { name: getTranslationString('billing.features.users', 'User Accounts'), included: true, limit: '50' },
        { name: getTranslationString('billing.features.support', '24/7 Support'), included: true },
        { name: getTranslationString('billing.features.analytics', 'Custom Analytics'), included: true },
        { name: getTranslationString('billing.features.customization', 'Custom Development'), included: true }
      ]
    }
  ], [getTranslationString]);

  // Formater la date
  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString();
  };

  // Formater les nombres avec des séparateurs de milliers
  const formatNumber = (num: number): string => {
    return new Intl.NumberFormat().format(num);
  };

  // Formater le pourcentage
  const formatPercentage = (num: number): string => {
    return `${num > 0 ? '+' : ''}${num.toFixed(2)}%`;
  };

  // Gérer le changement d'onglet
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // Fonctions de tri
  const handleRequestSort = (property: keyof Invoice) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  // Fonction pour filtrer les factures
  const getFilteredInvoices = () => {
    return invoices
      .filter(invoice => {
        // Filtre par statut
        if (filterStatus !== 'all' && invoice.status !== filterStatus) {
          return false;
        }
        
        // Filtre par recherche
        if (searchQuery) {
          const query = searchQuery.toLowerCase();
          return (
            invoice.invoiceNumber.toLowerCase().includes(query) ||
            invoice.amount.toLowerCase().includes(query) ||
            formatDate(invoice.date).toLowerCase().includes(query)
          );
        }
        
        return true;
      })
      .sort((a, b) => {
        // Tri
        if (orderBy === 'date') {
          const dateA = new Date(a.date).getTime();
          const dateB = new Date(b.date).getTime();
          return order === 'asc' ? dateA - dateB : dateB - dateA;
        } else if (orderBy === 'amount') {
          const amountA = parseFloat(a.amount.replace(/[^0-9.-]+/g, ''));
          const amountB = parseFloat(b.amount.replace(/[^0-9.-]+/g, ''));
          return order === 'asc' ? amountA - amountB : amountB - amountA;
        } else {
          const valueA = String(a[orderBy]).toLowerCase();
          const valueB = String(b[orderBy]).toLowerCase();
          return order === 'asc'
            ? valueA.localeCompare(valueB)
            : valueB.localeCompare(valueA);
        }
      });
  };

  // Réinitialiser les filtres
  const handleResetFilters = () => {
    setSearchQuery('');
    setFilterStatus('all');
    setOrderBy('date');
    setOrder('desc');
  };

  // Fonction pour filtrer les données d'utilisation
  const getFilteredUsageData = () => {
    return usageData
      .filter(item => {
        // Filtre par seuil d'utilisation
        if (usageFilterThreshold === 'high') {
          return item.percentage >= 80;
        } else if (usageFilterThreshold === 'medium') {
          return item.percentage >= 50 && item.percentage < 80;
        } else if (usageFilterThreshold === 'low') {
          return item.percentage < 50;
        }
        
        // Filtre par recherche
        if (usageSearchQuery) {
          const query = usageSearchQuery.toLowerCase();
          return item.service.toLowerCase().includes(query);
        }
        
        return true;
      })
      .sort((a, b) => b.percentage - a.percentage); // Tri par pourcentage d'utilisation décroissant
  };

  // Réinitialiser les filtres d'utilisation
  const handleResetUsageFilters = () => {
    setUsageSearchQuery('');
    setUsageFilterThreshold('all');
  };

  // Fonction pour ouvrir le dialogue de changement de plan
  const handleOpenPlanDialog = (plan: Plan) => {
    setSelectedPlan(plan);
    setOpenPlanDialog(true);
  };

  // Fonction pour obtenir la couleur du statut
  const getStatusColor = (status: string): string => {
    switch (status.toLowerCase()) {
      case 'paid':
        return 'success';
      case 'pending':
        return 'warning';
      case 'overdue':
        return 'error';
      default:
        return 'default';
    }
  };

  // Fonction pour formater la date
  const formatInvoiceDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  useEffect(() => {
    const fetchUserData = async () => {
      if (!currentUser) return;
      
      setLoading(true);
      try {
        // Récupérer le profil utilisateur depuis Firebase
        const profile = await getUserProfile(currentUser.uid);
        setUserProfile(profile);
        
        // Simuler les données de facturation
        setBillingInfo({
          currentPlan: 'Enterprise',
          planPrice: '€499.99/month',
          nextBilling: new Date(Date.now() + 2592000000).toISOString(), // Dans 30 jours
          paymentMethod: 'Credit Card',
          cardLastFour: '4242',
          cardExpiry: '12/25',
          billingAddress: '123 Business Ave, Tech City, 75001',
          autoRenew: true
        });
        
        // Simuler les factures avec plus de données
        setInvoices([
          {
            id: 'inv-001',
            invoiceNumber: 'INV-2023-10-001',
            date: new Date(Date.now() - 2592000000).toISOString(), // Il y a 30 jours
            amount: '€499.99',
            status: 'paid',
            items: [
              { description: 'Enterprise Plan - October 2023', quantity: 1, unitPrice: '€499.99', total: '€499.99' }
            ]
          },
          {
            id: 'inv-002',
            invoiceNumber: 'INV-2023-09-001',
            date: new Date(Date.now() - 5184000000).toISOString(), // Il y a 60 jours
            amount: '€499.99',
            status: 'paid',
            items: [
              { description: 'Enterprise Plan - September 2023', quantity: 1, unitPrice: '€499.99', total: '€499.99' }
            ]
          },
          {
            id: 'inv-003',
            invoiceNumber: 'INV-2023-08-001',
            date: new Date(Date.now() - 7776000000).toISOString(), // Il y a 90 jours
            amount: '€499.99',
            status: 'paid',
            items: [
              { description: 'Enterprise Plan - August 2023', quantity: 1, unitPrice: '€499.99', total: '€499.99' }
            ]
          },
          {
            id: 'inv-004',
            invoiceNumber: 'INV-2023-07-001',
            date: new Date(Date.now() - 10368000000).toISOString(), // Il y a 120 jours
            amount: '€499.99',
            status: 'paid',
            items: [
              { description: 'Enterprise Plan - July 2023', quantity: 1, unitPrice: '€499.99', total: '€499.99' }
            ]
          },
          {
            id: 'inv-005',
            invoiceNumber: 'INV-2023-11-001',
            date: new Date(Date.now() + 259200000).toISOString(), // Dans 3 jours
            amount: '€499.99',
            status: 'pending',
            items: [
              { description: 'Enterprise Plan - November 2023', quantity: 1, unitPrice: '€499.99', total: '€499.99' }
            ]
          },
          {
            id: 'inv-006',
            invoiceNumber: 'INV-2023-06-001',
            date: new Date(Date.now() - 12960000000).toISOString(), // Il y a 150 jours
            amount: '€399.99',
            status: 'paid',
            items: [
              { description: 'Professional Plan - June 2023', quantity: 1, unitPrice: '€399.99', total: '€399.99' }
            ]
          },
          {
            id: 'inv-007',
            invoiceNumber: 'INV-2023-05-001',
            date: new Date(Date.now() - 15552000000).toISOString(), // Il y a 180 jours
            amount: '€399.99',
            status: 'paid',
            items: [
              { description: 'Professional Plan - May 2023', quantity: 1, unitPrice: '€399.99', total: '€399.99' }
            ]
          }
        ]);
        
        // Simuler les données d'utilisation
        setUsageData([
          { service: 'API Calls', used: 850000, total: 1000000, percentage: 85 },
          { service: 'Storage', used: 75, total: 100, percentage: 75 },
          { service: 'User Accounts', used: 45, total: 50, percentage: 90 },
          { service: 'Bandwidth', used: 320, total: 500, percentage: 64 }
        ]);
        
      } catch (error) {
        console.error("Error fetching user data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, [currentUser]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
        <Typography ml={2}>{getTranslationString('common.loading', 'Loading...')}</Typography>
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h4" gutterBottom fontWeight="bold">
        {getTranslationString('billing.title', 'Billing & Invoices')} — Ares Code Works Hub
      </Typography>
      <Typography variant="body1" color="text.secondary" paragraph>
        {getTranslationString('billing.subtitle', 'Manage your subscription, payment methods, and view your billing history.')}
      </Typography>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          aria-label="billing tabs"
          textColor="primary"
          indicatorColor="primary"
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab 
            icon={<DashboardIcon />} 
            iconPosition="start" 
            label={getTranslationString('billing.tabs.overview', 'Overview')} 
          />
          <Tab 
            icon={<ReceiptLongIcon />} 
            iconPosition="start" 
            label={getTranslationString('billing.tabs.invoices', 'Invoices')} 
          />
          <Tab 
            icon={<DataUsageIcon />} 
            iconPosition="start" 
            label={getTranslationString('billing.tabs.usage', 'Usage')} 
          />
        </Tabs>
      </Box>

      {/* Overview Tab */}
      <Box role="tabpanel" hidden={tabValue !== 0}>
        {tabValue === 0 && (
          <Stack spacing={3}>
            {/* Première rangée: Support & Help et Add-ons côte à côte */}
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
              {/* Support & Aide */}
              <Box sx={{ flex: 1 }}>
                <Paper 
                  elevation={0} 
                  sx={{ 
                    p: 3,
                    height: '100%',
                    borderRadius: 3,
                    border: '1px solid',
                    borderColor: 'divider',
                    backgroundImage: 'url("/resources/canvas4.png")',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    position: 'relative',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      backgroundColor: 'rgba(26, 26, 26, 0.07)',
                      borderRadius: 3,
                      zIndex: 0
                    }
                  }}
                >
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      mb: 2,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      position: 'relative',
                      zIndex: 1
                    }}
                  >
                    <HelpIcon />
                    {getTranslationString('billing.supportHelp', 'Support & Help')}
                  </Typography>

                  <Stack spacing={2} sx={{ position: 'relative', zIndex: 1 }}>
                    <Stack direction="row" spacing={2} alignItems="flex-start">
                      <Avatar 
                        sx={{ 
                          bgcolor: 'primary.main',
                          width: 32,
                          height: 32
                        }}
                      >
                        <PhoneIcon fontSize="small" />
                      </Avatar>
                      <Box>
                        <Typography variant="body2" fontWeight="medium">
                          {getTranslationString('billing.prioritySupport', 'Priority Support')}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {getTranslationString('billing.responseTime', 'Response within 2 hours')}
                        </Typography>
                      </Box>
                    </Stack>

                    <Stack direction="row" spacing={2} alignItems="flex-start">
                      <Avatar 
                        sx={{ 
                          bgcolor: 'primary.main',
                          width: 32,
                          height: 32
                        }}
                      >
                        <LiveHelpIcon fontSize="small" />
                      </Avatar>
                      <Box>
                        <Typography variant="body2" fontWeight="medium">
                          {getTranslationString('billing.faq', 'FAQ')}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {getTranslationString('billing.helpCenter', 'Ares Code Works Help Center')}
                        </Typography>
                      </Box>
                    </Stack>

                    <Stack direction="row" spacing={2} alignItems="flex-start">
                      <Avatar 
                        sx={{ 
                          bgcolor: 'primary.main',
                          width: 32,
                          height: 32
                        }}
                      >
                        <EmailIcon fontSize="small" />
                      </Avatar>
                      <Box>
                        <Typography variant="body2" fontWeight="medium">
                          {getTranslationString('billing.contact', 'Contact')}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          billing@arescodeworks.com
                        </Typography>
                      </Box>
                    </Stack>

                    <Stack direction="row" spacing={2} alignItems="flex-start">
                      <Avatar 
                        sx={{ 
                          bgcolor: 'primary.main',
                          width: 32,
                          height: 32
                        }}
                      >
                        <ChatIcon fontSize="small" />
                      </Avatar>
                      <Box>
                        <Typography variant="body2" fontWeight="medium">
                          {getTranslationString('billing.liveChat', 'Live Chat')}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {getTranslationString('billing.chatHours', 'Available daily from 9AM to 8PM')}
                        </Typography>
                      </Box>
                    </Stack>
                  </Stack>

                  <Button 
                    variant="contained" 
                    color="primary"
                    fullWidth
                    sx={{ mt: 2, position: 'relative', zIndex: 1 }}
                  >
                    {getTranslationString('billing.contactSupport', 'Contact Support')}
                  </Button>
                </Paper>
              </Box>

              {/* Add-ons & Options */}
              <Box sx={{ flex: 1 }}>
                <Paper 
                  elevation={0} 
                  sx={{ 
                    p: 3, 
                    height: '100%',
                    borderRadius: 3,
                    border: '1px solid',
                    borderColor: 'divider'
                  }}
                >
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      mb: 3,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1
                    }}
                  >
                    <AddCircleIcon />
                    {getTranslationString('billing.addOnsOptions', 'Add-ons')}
                  </Typography>

                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2 }}>
                    {addOns.map((addon, index) => (
                      <Paper
                        key={index}
                        elevation={0}
                        sx={{
                          p: 2,
                          borderRadius: 2,
                          border: '1px solid',
                          borderColor: 'divider',
                          display: 'flex',
                          flexDirection: 'column',
                          height: '100%'
                        }}
                      >
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                          <Avatar
                            sx={{
                              bgcolor: 'primary.lighter',
                              color: 'primary.main',
                              width: 32,
                              height: 32
                            }}
                          >
                            {addon.icon}
                          </Avatar>
                          <Typography variant="body1" fontWeight="medium" noWrap>
                            {addon.name}
                          </Typography>
                        </Stack>
                        
                        {addon.description && (
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            {addon.description}
                          </Typography>
                        )}
                        
                        <Box sx={{ mt: 'auto' }}>
                          <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Typography variant="body2" fontWeight="medium" color="primary.main">
                              {addon.price}
                            </Typography>
                            <Button
                              variant="outlined"
                              size="small"
                            >
                              {getTranslationString('billing.addModule', 'Add')}
                            </Button>
                          </Stack>
                        </Box>
                      </Paper>
                    ))}
                  </Box>
                </Paper>
              </Box>
            </Stack>

            {/* Contenu principal */}
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
              {/* Première colonne */}
              <Box sx={{ width: { xs: '100%', md: '33.33%' } }}>
                {/* Identité utilisateur */}
                <Paper 
                  elevation={0} 
                  sx={{ 
                    p: 3, 
                    mb: 3, 
                    borderRadius: 3,
                    border: '1px solid',
                    borderColor: 'divider'
                  }}
                >
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      mb: 2,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1
                    }}
                  >
                    <PersonIcon />
                    {getTranslationString('billing.userIdentity', 'User Identity')}
                  </Typography>

                  <Stack spacing={2}>
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        {getTranslationString('billing.fullName', 'Full Name')}
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {userData.fullName}
                      </Typography>
                    </Box>

                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        {getTranslationString('billing.company', 'Company')}
                      </Typography>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <BusinessIcon fontSize="small" color="action" />
                        <Typography variant="body1">
                          {userData.company}
                        </Typography>
                      </Stack>
                    </Box>

                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        {getTranslationString('billing.email', 'Email')}
                      </Typography>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <EmailIcon fontSize="small" color="action" />
                        <Typography variant="body1">
                          {userData.email}
                        </Typography>
                      </Stack>
                    </Box>

                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        {getTranslationString('billing.accountCreated', 'Account Created')}
                      </Typography>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <EventIcon fontSize="small" color="action" />
                        <Typography variant="body1">
                          {new Date(userData.createdAt).toLocaleDateString()}
                        </Typography>
                      </Stack>
                    </Box>

                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        {getTranslationString('billing.accountStatus', 'Account Status')}
                      </Typography>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <VerifiedIcon fontSize="small" color="success" />
                        <Typography variant="body1" color="success.main">
                          {getTranslationString('billing.verified', 'Verified')}
                        </Typography>
                      </Stack>
                    </Box>
                  </Stack>
                </Paper>

                {/* Méthode de paiement */}
                <Paper 
                  elevation={0} 
                  sx={{ 
                    p: 3, 
                    mb: 3, 
                    borderRadius: 3,
                    border: '1px solid',
                    borderColor: 'divider'
                  }}
                >
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      mb: 2,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1
                    }}
                  >
                    <CreditCardIcon />
                    {getTranslationString('billing.paymentMethod', 'Payment Method')}
                  </Typography>

                  <Stack spacing={2}>
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        {getTranslationString('billing.mainCard', 'Main Card')}
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        Visa ***8492
                      </Typography>
                    </Box>

                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        {getTranslationString('billing.expires', 'Expires')}
                      </Typography>
                      <Typography variant="body1">
                        08/27
                      </Typography>
                    </Box>

                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        {getTranslationString('billing.nextCharge', 'Next Charge')}
                      </Typography>
                      <Typography variant="body1">
                        {new Date(subscriptionData.nextDate).toLocaleDateString()} à 10h00
                      </Typography>
                    </Box>

                    <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                      <Button 
                        variant="outlined" 
                        size="small"
                        startIcon={<EditIcon />}
                      >
                        {getTranslationString('billing.editCard', 'Edit Card')}
                      </Button>
                      <Button 
                        variant="outlined" 
                        size="small"
                        startIcon={<AddIcon />}
                      >
                        {getTranslationString('billing.addMethod', 'Add Method')}
                      </Button>
                    </Stack>
                  </Stack>
                </Paper>

                {/* Notifications & Alerts */}
                <Paper 
                  elevation={0} 
                  sx={{ 
                    p: 3,
                    mb: 3,
                    borderRadius: 3,
                    border: '1px solid',
                    borderColor: 'divider'
                  }}
                >
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      mb: 3,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1
                    }}
                  >
                    <NotificationsIcon />
                    {getTranslationString('billing.notificationsAlerts', 'Notifications & Alerts')}
                  </Typography>

                  <List>
                    {notifications.map((notification, index) => (
                      <ListItem
                        key={index}
                        sx={{
                          mb: 1,
                          borderRadius: 2,
                          bgcolor: 'background.default',
                          '&:last-child': { mb: 0 }
                        }}
                      >
                        <ListItemIcon>
                          {notification.icon}
                        </ListItemIcon>
                        <ListItemText
                          primary={notification.message}
                          secondary={new Date(notification.date).toLocaleDateString()}
                        />
                      </ListItem>
                    ))}
                    
                    <ListItem
                      sx={{
                        borderRadius: 2,
                        bgcolor: 'success.lighter'
                      }}
                    >
                      <ListItemIcon>
                        <CheckCircleIcon color="success" />
                      </ListItemIcon>
                      <ListItemText
                        primary={getTranslationString('billing.noCriticalAlerts', 'No critical alerts')}
                        secondary={getTranslationString('billing.everythingOk', 'Everything is working correctly')}
                      />
                    </ListItem>
                  </List>
                </Paper>
              </Box>

              {/* Deuxième colonne */}
              <Box sx={{ width: { xs: '100%', md: '66.67%' } }}>
                {/* Plan d'abonnement */}
                <Paper 
                  elevation={0} 
                  sx={{ 
                    p: 3, 
                    mb: 3, 
                    borderRadius: 3,
                    border: '1px solid',
                    borderColor: 'primary.main',
                    position: 'relative',
                    overflow: 'hidden'
                  }}
                >
                  <Box 
                    sx={{
                      position: 'absolute',
                      top: 0,
                      right: 0,
                      bgcolor: 'primary.main',
                      color: 'white',
                      px: 2,
                      py: 0.5,
                      borderBottomLeftRadius: 8
                    }}
                  >
                    {getTranslationString('billing.active', 'Active')}
                  </Box>

                  <Typography 
                    variant="h6" 
                    sx={{ 
                      mb: 3,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1
                    }}
                  >
                    <RocketIcon />
                    {getTranslationString('billing.subscriptionPlan', 'Subscription Plan')}
                  </Typography>

                  <Stack 
                    direction={{ xs: 'column', md: 'row' }} 
                    spacing={2} 
                    sx={{ mb: 3 }}
                  >
                    <Box sx={{ width: { xs: '100%', md: '50%' } }}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <RocketIcon color="primary" />
                        <Typography variant="h5" color="primary.main" fontWeight="bold">
                          {subscriptionData.plan}
                        </Typography>
                      </Stack>
                      <Typography variant="body2" color="text.secondary" sx={{ ml: 4 }}>
                        {getTranslationString('billing.activePlan', 'Active Plan')}
                      </Typography>
                    </Box>
                    <Box sx={{ width: { xs: '100%', md: '50%' } }}>
                      <Typography variant="h5" fontWeight="bold" align="right">
                        {subscriptionData.price}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" align="right">
                        {subscriptionData.type} • {subscriptionData.renewal === "Automatique" ? 
                          getTranslationString('billing.autoRenew', 'Auto-Renew') : 
                          getTranslationString('billing.manualRenew', 'Manual Renew')}
                      </Typography>
                    </Box>
                  </Stack>

                  <Box sx={{ mb: 3 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography variant="body2" color="text.secondary">
                        {getTranslationString('billing.nextBilling', 'Next Billing')}
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {new Date(subscriptionData.nextDate).toLocaleDateString()}
                      </Typography>
                    </Stack>
                  </Box>

                  <Divider sx={{ mb: 3 }} />

                  <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
                    {getTranslationString('billing.includedFeatures', 'Included in this plan')}:
                  </Typography>

                  <Box sx={{ mb: 3 }}>
                    <Stack spacing={2}>
                      {planFeatures.map((feature, index) => (
                        <Box key={index} sx={{ width: { xs: '100%', md: '50%' }, display: 'inline-block', pr: 2 }}>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <CheckCircleIcon color="success" fontSize="small" />
                            <Typography variant="body2">
                              {feature}
                            </Typography>
                          </Stack>
                        </Box>
                      ))}
                    </Stack>
                  </Box>

                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    {getTranslationString('billing.needMore', 'Need more?')}
                  </Typography>

                  <Stack direction="row" spacing={2}>
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={<TrendingUpIcon />}
                    >
                      {getTranslationString('billing.upgradeBusiness', 'Upgrade to Business')}
                    </Button>
                    <Button
                      variant="outlined"
                      color="error"
                      startIcon={<CancelIcon />}
                    >
                      {getTranslationString('billing.cancelSubscription', 'Cancel Subscription')}
                    </Button>
                  </Stack>
                </Paper>

                {/* Historique de facturation */}
                <Paper 
                  elevation={0} 
                  sx={{ 
                    p: 3, 
                    mb: 3, 
                    borderRadius: 3,
                    border: '1px solid',
                    borderColor: 'divider'
                  }}
                >
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      mb: 3,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1
                    }}
                  >
                    <ReceiptIcon />
                    {getTranslationString('billing.billingHistory', 'Billing History')}
                  </Typography>

                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>{getTranslationString('billing.date', 'Date')}</TableCell>
                          <TableCell>{getTranslationString('billing.description', 'Description')}</TableCell>
                          <TableCell align="right">{getTranslationString('billing.amount', 'Amount')}</TableCell>
                          <TableCell>{getTranslationString('billing.status', 'Status')}</TableCell>
                          <TableCell align="center">{getTranslationString('billing.actions', 'Actions')}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {recentInvoices.map((invoice) => (
                          <TableRow key={invoice.id}>
                            <TableCell>{new Date(invoice.date).toLocaleDateString()}</TableCell>
                            <TableCell>{invoice.description}</TableCell>
                            <TableCell align="right">
                              {new Intl.NumberFormat(undefined, {
                                style: 'currency',
                                currency: 'EUR'
                              }).format(invoice.amount)}
                            </TableCell>
                            <TableCell>
                              <Chip 
                                icon={<CheckCircleIcon fontSize="small" />} 
                                label={getTranslationString('billing.status.paid', 'Paid')}
                                size="small"
                                color="success"
                              />
                            </TableCell>
                            <TableCell align="center">
                              <IconButton size="small">
                                <GetAppIcon fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  <Button
                    variant="text"
                    color="primary"
                    endIcon={<FolderIcon />}
                    sx={{ mt: 2 }}
                  >
                    {getTranslationString('billing.accessAllInvoices', 'Access All Invoices')}
                  </Button>
                </Paper>


              </Box>
            </Stack>
          </Stack>
        )}
      </Box>

      {/* Invoices Tab */}
      <Box role="tabpanel" hidden={tabValue !== 1}>
        {tabValue === 1 && (
          <Paper 
            elevation={0} 
            sx={{ 
              p: 3, 
              borderRadius: 3,
              border: '1px solid',
              borderColor: 'divider'
            }}
          >
            <Typography 
              variant="h6" 
              sx={{ 
                mb: 3,
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}
            >
              <ReceiptIcon />
              {getTranslationString('billing.invoiceHistory', 'Invoice History')}
            </Typography>
            
            {/* Barre de recherche et filtres */}
            <Box sx={{ mb: 3, display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
              <TextField
                placeholder={getTranslationString('billing.search', 'Search invoices...')}
                variant="outlined"
                size="small"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                sx={{ flexGrow: 1, minWidth: '200px' }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                  endAdornment: searchQuery ? (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setSearchQuery('')}>
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ) : null
                }}
              />
              
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel id="status-filter-label">{getTranslationString('billing.filterStatus', 'Status')}</InputLabel>
                <Select
                  labelId="status-filter-label"
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  input={<OutlinedInput label={getTranslationString('billing.filterStatus', 'Status')} />}
                >
                  <MenuItem value="all">{getTranslationString('billing.filterAll', 'All')}</MenuItem>
                  <MenuItem value="paid">{getTranslationString('billing.status.paid', 'Paid')}</MenuItem>
                  <MenuItem value="pending">{getTranslationString('billing.status.pending', 'Pending')}</MenuItem>
                  <MenuItem value="overdue">{getTranslationString('billing.status.overdue', 'Overdue')}</MenuItem>
                </Select>
              </FormControl>
              
              <Tooltip title={getTranslationString('billing.resetFilters', 'Reset Filters')}>
                <IconButton onClick={handleResetFilters} size="small" color="primary">
                  <FilterListIcon />
                </IconButton>
              </Tooltip>
            </Box>
            
            <TableContainer>
              <Table sx={{ minWidth: 650 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <TableSortLabel
                        active={orderBy === 'invoiceNumber'}
                        direction={orderBy === 'invoiceNumber' ? order : 'asc'}
                        onClick={() => handleRequestSort('invoiceNumber')}
                      >
                        {getTranslationString('billing.invoice', 'Invoice')}
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={orderBy === 'date'}
                        direction={orderBy === 'date' ? order : 'asc'}
                        onClick={() => handleRequestSort('date')}
                      >
                        {getTranslationString('billing.date', 'Date')}
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={orderBy === 'amount'}
                        direction={orderBy === 'amount' ? order : 'asc'}
                        onClick={() => handleRequestSort('amount')}
                      >
                        {getTranslationString('billing.amount', 'Amount')}
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={orderBy === 'status'}
                        direction={orderBy === 'status' ? order : 'asc'}
                        onClick={() => handleRequestSort('status')}
                      >
                        {getTranslationString('billing.status', 'Status')}
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="right">{getTranslationString('billing.actions', 'Actions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {getFilteredInvoices().length > 0 ? (
                    getFilteredInvoices().map((invoice) => (
                      <TableRow key={invoice.id}>
                        <TableCell>{invoice.invoiceNumber}</TableCell>
                        <TableCell>{formatDate(invoice.date)}</TableCell>
                        <TableCell>{invoice.amount}</TableCell>
                        <TableCell>
                          <Chip 
                            label={
                              invoice.status === 'paid' 
                                ? getTranslationString('billing.status.paid', 'Paid') 
                                : invoice.status === 'pending' 
                                  ? getTranslationString('billing.status.pending', 'Pending') 
                                  : getTranslationString('billing.status.overdue', 'Overdue')
                            } 
                            color={
                              invoice.status === 'paid' 
                                ? 'success' 
                                : invoice.status === 'pending' 
                                  ? 'warning' 
                                  : 'error'
                            }
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                            <Button 
                              variant="outlined" 
                              size="small"
                              startIcon={<DescriptionIcon />}
                            >
                              {getTranslationString('billing.view', 'View')}
                            </Button>
                            <Button 
                              variant="contained" 
                              size="small"
                              startIcon={<CloudDownloadIcon />}
                            >
                              {getTranslationString('billing.download', 'Download')}
                            </Button>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                        <Typography variant="body1" color="text.secondary">
                          {getTranslationString('billing.noInvoicesFound', 'No invoices found matching your criteria')}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )}
      </Box>

      {/* Usage Tab */}
      <Box role="tabpanel" hidden={tabValue !== 2}>
        {tabValue === 2 && (
          <Paper 
            elevation={0} 
            sx={{ 
              p: 3, 
              borderRadius: 3,
              border: '1px solid',
              borderColor: 'divider'
            }}
          >
            <Typography 
              variant="h6" 
              sx={{ 
                mb: 3,
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}
            >
              <HistoryIcon />
              {getTranslationString('billing.usageStats', 'Usage Statistics')}
            </Typography>
            
            {/* Barre de recherche et filtres pour l'utilisation */}
            <Box sx={{ mb: 3, display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
              <TextField
                placeholder={getTranslationString('billing.searchUsage', 'Search services...')}
                variant="outlined"
                size="small"
                value={usageSearchQuery}
                onChange={(e) => setUsageSearchQuery(e.target.value)}
                sx={{ flexGrow: 1, minWidth: '200px' }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                  endAdornment: usageSearchQuery ? (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setUsageSearchQuery('')}>
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ) : null
                }}
              />
              
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel id="usage-filter-label">{getTranslationString('billing.filterUsage', 'Usage Level')}</InputLabel>
                <Select
                  labelId="usage-filter-label"
                  value={usageFilterThreshold}
                  onChange={(e) => setUsageFilterThreshold(e.target.value)}
                  input={<OutlinedInput label={getTranslationString('billing.filterUsage', 'Usage Level')} />}
                >
                  <MenuItem value="all">{getTranslationString('billing.filterAll', 'All')}</MenuItem>
                  <MenuItem value="high">{getTranslationString('billing.usageHigh', 'High (≥80%)')}</MenuItem>
                  <MenuItem value="medium">{getTranslationString('billing.usageMedium', 'Medium (50-79%)')}</MenuItem>
                  <MenuItem value="low">{getTranslationString('billing.usageLow', 'Low (<50%)')}</MenuItem>
                </Select>
              </FormControl>
              
              <Tooltip title={getTranslationString('billing.resetFilters', 'Reset Filters')}>
                <IconButton onClick={handleResetUsageFilters} size="small" color="primary">
                  <FilterListIcon />
                </IconButton>
              </Tooltip>
            </Box>
            
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
              {getFilteredUsageData().length > 0 ? (
                getFilteredUsageData().map((item, index) => (
                  <Box key={index} sx={{ flexGrow: 1, flexBasis: { xs: '100%', sm: 'calc(50% - 1.5rem)' } }}>
                    <Card sx={{ p: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
                      <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
                        {item.service}
                      </Typography>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                          {getTranslationString('billing.used', 'Used')}: {item.used} {item.service === 'Storage' ? 'GB' : item.service === 'Bandwidth' ? 'GB' : ''}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {getTranslationString('billing.total', 'Total')}: {item.total} {item.service === 'Storage' ? 'GB' : item.service === 'Bandwidth' ? 'GB' : ''}
                        </Typography>
                      </Box>
                      <LinearProgress 
                        variant="determinate" 
                        value={item.percentage} 
                        sx={{ 
                          height: 8, 
                          borderRadius: 4,
                          mb: 1,
                          backgroundColor: 'rgba(0,0,0,0.05)',
                          '& .MuiLinearProgress-bar': {
                            borderRadius: 4,
                            backgroundColor: item.percentage > 90 ? 'error.main' : 
                                            item.percentage > 75 ? 'warning.main' : 'success.main'
                          }
                        }} 
                      />
                      <Typography variant="body2" align="right" fontWeight={500} color={
                        item.percentage > 90 ? 'error.main' : 
                        item.percentage > 75 ? 'warning.main' : 'success.main'
                      }>
                        {item.percentage}%
                      </Typography>
                    </Card>
                  </Box>
                ))
              ) : (
                <Box sx={{ width: '100%', textAlign: 'center', py: 4 }}>
                  <Typography variant="body1" color="text.secondary">
                    {getTranslationString('billing.noUsageDataFound', 'No usage data found matching your criteria')}
                  </Typography>
                </Box>
              )}
            </Box>
            
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
              <Button 
                variant="contained" 
                color="primary"
                startIcon={<AttachMoneyIcon />}
              >
                {getTranslationString('billing.upgradePlan', 'Upgrade Plan')}
              </Button>
            </Box>
          </Paper>
        )}
      </Box>

      {/* Dialog de changement de plan */}
      <Dialog 
        open={openPlanDialog} 
        onClose={() => setOpenPlanDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {getTranslationString('billing.comparePlans', 'Compare Plans')}
        </DialogTitle>
        <DialogContent>
          <Stack 
            direction={{ xs: 'column', sm: 'row' }} 
            spacing={2} 
            sx={{ mt: 1 }}
          >
            {plans.map((plan) => (
              <Box key={plan.id} sx={{ flex: 1 }}>
                <Card 
                  sx={{ 
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    position: 'relative',
                    border: plan.recommended ? '2px solid' : '1px solid',
                    borderColor: plan.recommended ? 'primary.main' : 'divider'
                  }}
                >
                  {plan.recommended && (
                    <Chip
                      label={getTranslationString('billing.recommended', 'Recommended')}
                      color="primary"
                      size="small"
                      sx={{
                        position: 'absolute',
                        top: -12,
                        right: 16
                      }}
                    />
                  )}
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Typography variant="h6" gutterBottom>
                      {plan.name}
                    </Typography>
                    <Typography variant="h4" color="primary" gutterBottom>
                      {plan.price}
                      <Typography component="span" variant="body2" color="text.secondary">
                        {plan.billingPeriod}
                      </Typography>
                    </Typography>
                    <Typography variant="body2" color="text.secondary" paragraph>
                      {plan.description}
                    </Typography>
                    <Divider sx={{ my: 2 }} />
                    <List dense>
                      {plan.features.map((feature, index) => (
                        <ListItem key={index} sx={{ px: 0 }}>
                          <ListItemIcon sx={{ minWidth: 36 }}>
                            <CheckCircleIcon 
                              fontSize="small"
                              color={feature.included ? 'success' : 'disabled'}
                            />
                          </ListItemIcon>
                          <ListItemText 
                            primary={feature.name}
                            secondary={feature.limit}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </CardContent>
                  <Box sx={{ p: 2, pt: 0 }}>
                    <Button
                      variant={plan.id.toLowerCase() === billingInfo?.currentPlan.toLowerCase() ? 'outlined' : 'contained'}
                      color="primary"
                      fullWidth
                      disabled={plan.id.toLowerCase() === billingInfo?.currentPlan.toLowerCase()}
                    >
                      {plan.id.toLowerCase() === billingInfo?.currentPlan.toLowerCase()
                        ? getTranslationString('billing.currentPlan', 'Current Plan')
                        : getTranslationString('billing.selectPlan', 'Select Plan')}
                    </Button>
                  </Box>
                </Card>
              </Box>
            ))}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenPlanDialog(false)}>
            {getTranslationString('common.close', 'Close')}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default BillingClient; 
